import React, { useEffect, useState } from 'react';
import { useAuth } from './context/AuthContext';
import { getMainAxios } from './utils/util';
import LoginPage from './components/LoginPage';
import PortfolioOverview from './components/PortfolioOverview';
import StockMaster from './components/StockMaster';
import EtfAnalysis from './components/EtfAnalysis';
import EtfPdf from './components/EtfPdf';
import TopRiseRate from './components/TopRiseRate';
import PortfolioManagement from './components/PortfolioManagement';
import AccountAdmin from './components/AccountAdmin';
import PortfolioReview from './components/PortfolioReview';
import StrategySlotReview from './components/StrategySlotReview';
import CurrentPrice from './components/CurrentPrice';
import Trading from './components/Trading';
import WatchlistManagement from './components/WatchlistManagement';
import CronJobManagement from './components/CronJobManagement';
import UserManagement from './components/UserManagement';
import QuantSignals from './components/QuantSignals';
import QuantSimulate from './components/QuantSimulate';
import QuantIntraday from './components/QuantIntraday';
import QuantIntradayMonitor from './components/QuantIntradayMonitor';
import QuantMicroSignal from './components/QuantMicroSignal';
import QuantBacktest from './components/QuantBacktest';
import StrategyManagement from './components/StrategyManagement';
import SimHistory from './components/SimHistory';
import StockScreener from './components/StockScreener';
import DataCollectionMonitor from './components/DataCollectionMonitor';
import BizDayManagement from './components/BizDayManagement';
import StockData from './components/StockData';
import FuturesData from './components/FuturesData';
import QuantCursor from './components/QuantCursor';
import NaverTheme from './components/NaverTheme';
import TradingJournal from './components/TradingJournal';
import ResearchSystems from './components/ResearchSystems';
import ResearchBriefing from './components/ResearchBriefing';
import ResearchEvidence from './components/ResearchEvidence';
import AgentDashboard from './components/AgentDashboard';
import LogViewer from './components/LogViewer';
import { FdsNavProvider } from './context/FdsNavContext';
import LlmChat from './components/LlmChat';
import CursorAsk from './components/CursorAsk';
import FillAnalysis from './components/FillAnalysis';
import TradingViewSample from './components/TradingViewSample';
import BentoDashboard from './components/BentoDashboard';
import DarkInvestDashboard from './components/DarkInvestDashboard';
import './App.css';

type PageKey = 'orderposition' | 'tvsample' | 'bento' | 'darkinvest' | 'portfolioreview' | 'strategyslot' | 'fillanalysis' | 'trading' | 'currentprice' | 'stockmaster' | 'etfanalysis' | 'etfpdf' | 'topriserate' | 'strategymanagement' | 'quantsignals' | 'quantsimulate' | 'quantintraday' | 'quantintradaymonitor' | 'quantmicrosignal' | 'quantbacktest' | 'tradingjournal' | 'researchsystems' | 'researchmarketbrief' | 'researchalgoreport' | 'researchevidence' | 'agentdashboard' | 'logviewer' | 'quantcursor' | 'navertheme' | 'simhistory' | 'stockscreener' | 'portfoliomanagement' | 'acctadmin' | 'watchlist' | 'cronjob' | 'datacollection' | 'datastock' | 'datasfut' | 'dataifut' | 'llm' | 'cursorask' | 'bizday' | 'usermanagement' | 'changepassword';

interface MenuItem {
  key: PageKey;
  label: string;
}

interface MenuGroup {
  key: string;
  label: string;
  items: MenuItem[];
}

const menuGroups: MenuGroup[] = [
  {
    key: 'portfolio', label: '포트폴리오',
    items: [
      { key: 'orderposition', label: '포트폴리오' },
      { key: 'portfolioreview', label: '포트폴리오복기' },
      { key: 'strategyslot', label: '전략별 성과' },
      { key: 'fillanalysis', label: '체결 현황분석' },
      { key: 'tvsample', label: '트레이딩' },
      { key: 'bento', label: '투자 대시보드(샘플)' },
      { key: 'darkinvest', label: '다크 대시보드(샘플)' },
    ],
  },
  {
    key: 'order', label: '종목분석',
    items: [
      { key: 'trading', label: '주문' },
      { key: 'currentprice', label: '현재가' },
      { key: 'stockmaster', label: '종목정보' },
      { key: 'etfanalysis', label: 'ETF분석' },
      { key: 'etfpdf', label: 'ETFPDF' },
      { key: 'topriserate', label: '상승률상위' },
    ],
  },
  {
    key: 'quant', label: 'Quant',
    items: [
      { key: 'strategymanagement', label: '전략관리' },
      { key: 'quantsignals', label: '전략신호' },
      { key: 'quantsimulate', label: '시뮬레이션' },
      { key: 'quantintraday', label: '인트라데이' },
      { key: 'quantintradaymonitor', label: '인트라데이 모니터' },
      { key: 'quantmicrosignal', label: '인트라데이 신호분석' },
      { key: 'quantbacktest', label: '백테스트' },
      { key: 'quantcursor', label: 'Quant(커서)' },
      { key: 'navertheme', label: '테마별시세' },
      { key: 'simhistory', label: '시뮬레이션이력' },
      { key: 'stockscreener', label: '스크리너' },
    ],
  },
  {
    key: 'research', label: 'Research',
    items: [
      { key: 'tradingjournal', label: '매매일지 (Journal)' },
      { key: 'researchsystems', label: '시스템 (Systems)' },
      { key: 'researchmarketbrief', label: '일별 시황 (Briefings)' },
      { key: 'researchalgoreport', label: '알고리매매 (Algo)' },
      { key: 'researchevidence', label: '검증·근거 (Evidence)' },
    ],
  },
  {
    // Data — 수집 데이터 현황/관리 전용 메뉴
    key: 'data', label: 'Data',
    items: [
      { key: 'datacollection', label: '데이터수집현황' },
      { key: 'cronjob', label: 'CronJob' },
      { key: 'bizday', label: '영업일관리' },
      { key: 'datastock', label: '주식' },
      { key: 'datasfut', label: '주식선물' },
      { key: 'dataifut', label: '지수선물' },
      { key: 'watchlist', label: '관심종목' },
    ],
  },
  {
    key: 'agent', label: 'Agent',
    items: [
      { key: 'logviewer', label: 'LogViewer' },
      { key: 'agentdashboard', label: 'Agent 판정' },
    ],
  },
  {
    // LLM — Ollama / Open WebUI. 설계: docs/design/LLM-단일모델-운용안.md
    key: 'llm', label: 'LLM',
    items: [
      { key: 'llm', label: 'AI 채팅' },
      { key: 'cursorask', label: '커서 요청' },
    ],
  },
  {
    key: 'admin', label: 'Admin',
    items: [
      // 계좌·키·전략·외부 사용자를 한 화면에서 (2026-11-03). 구 KIS계정관리(portfoliomanagement)는 메뉴에서 뺐다 — 키는 '키 교체'로만 바꾼다
      { key: 'acctadmin', label: '계좌·전략 관리' },
      { key: 'usermanagement', label: '계정관리' },
      { key: 'changepassword', label: '비밀번호변경' },
    ],
  },
];

const pageComponents: Record<PageKey, React.ReactNode | null> = {
  trading: <Trading />,
  orderposition: <PortfolioOverview />,
  tvsample: <TradingViewSample />,
  bento: <BentoDashboard />,
  darkinvest: <DarkInvestDashboard />,
  portfolioreview: <PortfolioReview />,
  strategyslot: <StrategySlotReview />,
  fillanalysis: <FillAnalysis />,
  stockmaster: <StockMaster />,
  etfanalysis: <EtfAnalysis />,
  etfpdf: <EtfPdf />,
  topriserate: <TopRiseRate />,
  currentprice: <CurrentPrice />,
  portfoliomanagement: <PortfolioManagement />,
    acctadmin: <AccountAdmin />,
  watchlist: <WatchlistManagement />,
  strategymanagement: <StrategyManagement />,
  quantsignals: <QuantSignals />,
  quantsimulate: <QuantSimulate />,
  quantintraday: <QuantIntraday />,
  quantintradaymonitor: <QuantIntradayMonitor />,
  quantmicrosignal: <QuantMicroSignal />,
  quantbacktest: <QuantBacktest />,
  quantcursor: <QuantCursor />,
  navertheme: <NaverTheme />,
  simhistory: <SimHistory />,
  stockscreener: <StockScreener />,
  cronjob: <CronJobManagement />,
  datacollection: <DataCollectionMonitor />,
  bizday: <BizDayManagement />,
  datastock: <StockData />,
  datasfut: <FuturesData futType="stk" />,
  dataifut: <FuturesData futType="idx" />,
  tradingjournal: <TradingJournal />,
  researchsystems: <ResearchSystems />,
  researchmarketbrief: (
    <ResearchBriefing
      kind="market"
      title="일별 시황 (Briefings)"
      subtitle="17:00 자동 생성 + Cursor market-brief 서술 보완"
    />
  ),
  researchalgoreport: (
    <ResearchBriefing
      kind="algo"
      title="알고리매매 (Algo Reports)"
      subtitle="ordersrc별 주문·체결·NAV — fill-reporter 해석"
    />
  ),
  researchevidence: <ResearchEvidence />,
  logviewer: <LogViewer />,
  agentdashboard: <AgentDashboard />,
  llm: <LlmChat />,
  cursorask: <CursorAsk />,
  usermanagement: <UserManagement />,
  changepassword: null, // 모달로 처리
};

/**
 * 비밀번호 변경. forced = 임시 비밀번호로 로그인한 상태 — 닫을 수 없고(로그아웃만), 바꾸면 서버가 준
 * 제한 없는 새 토큰으로 갈아 끼운다 (임시 토큰으로는 다른 API 가 403 이다).
 */
function ChangePasswordModal({ onClose, forced = false }: { onClose: () => void; forced?: boolean }) {
  const { logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== newPasswordConfirm) {
      setError('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    if (newPassword.length < 6) {
      setError('새 비밀번호는 6자 이상이어야 합니다.');
      return;
    }

    setSubmitting(true);
    try {
      const axios = getMainAxios();
      const res = await axios.post('/api/v1/auth/change-password', { currentPassword, newPassword });
      if (res.data?.success) {
        if (forced && res.data.token) {
          localStorage.setItem('fds_token', res.data.token);
          window.location.reload();
          return;
        }
        setSuccess(true);
      } else {
        setError(res.data?.error || '비밀번호 변경에 실패했습니다.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || '비밀번호 변경에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      justifyContent: 'center', alignItems: 'center', zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '8px', padding: '24px',
        width: '360px', boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
      }}>
        <h3 style={{ margin: '0 0 16px', color: '#1e3a8a', fontSize: '14px', fontWeight: '700' }}>
          {forced ? '새 비밀번호를 정하세요' : '비밀번호 변경'}
        </h3>
        {forced && (
          <div style={{
            padding: '8px 10px', backgroundColor: '#fef3c7', border: '1px solid #f59e0b',
            borderRadius: '6px', color: '#92400e', fontSize: '12px', marginBottom: '12px',
          }}>
            임시 비밀번호로 로그인했습니다. 새 비밀번호로 바꿔야 화면을 쓸 수 있습니다.
            '현재 비밀번호'에는 받은 임시 비밀번호를 넣으세요.
          </div>
        )}

        {success ? (
          <div>
            <div style={{
              padding: '12px', backgroundColor: '#dcfce7', border: '1px solid #86efac',
              borderRadius: '6px', color: '#166534', fontSize: '12px', marginBottom: '16px',
            }}>
              비밀번호가 변경되었습니다.
            </div>
            <button onClick={onClose} style={{
              width: '100%', padding: '10px', backgroundColor: '#2563eb', color: 'white',
              border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
            }}>
              닫기
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#1e40af', marginBottom: '4px' }}>
                현재 비밀번호
              </label>
              <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required
                style={{ width: '100%', padding: '8px 10px', border: '2px solid #d1d5db', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#1e40af', marginBottom: '4px' }}>
                새 비밀번호
              </label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required
                style={{ width: '100%', padding: '8px 10px', border: '2px solid #d1d5db', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#1e40af', marginBottom: '4px' }}>
                새 비밀번호 확인
              </label>
              <input type="password" value={newPasswordConfirm} onChange={e => setNewPasswordConfirm(e.target.value)} required
                style={{ width: '100%', padding: '8px 10px', border: '2px solid #d1d5db', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}
              />
            </div>

            {error && (
              <div style={{
                padding: '8px', backgroundColor: '#fee2e2', border: '1px solid #fca5a5',
                borderRadius: '6px', color: '#991b1b', fontSize: '12px', marginBottom: '12px',
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" onClick={forced ? logout : onClose} style={{
                flex: 1, padding: '10px', backgroundColor: '#6b7280', color: 'white',
                border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
              }}>
                {forced ? '로그아웃' : '취소'}
              </button>
              <button type="submit" disabled={submitting} style={{
                flex: 1, padding: '10px', backgroundColor: submitting ? '#93c5fd' : '#2563eb', color: 'white',
                border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}>
                {submitting ? '변경 중...' : '변경'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function AdminPasswordModal({ onSuccess, onClose }: { onSuccess: () => void; onClose: () => void }) {
  const { user } = useAuth();
  const [pwd, setPwd] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const axios = getMainAxios();
      await axios.post('/api/v1/auth/validate-credentials', { email: user!.email, password: pwd });
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || '비밀번호가 일치하지 않습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      justifyContent: 'center', alignItems: 'center', zIndex: 2000,
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '8px', padding: '24px',
        width: '320px', boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
      }}>
        <h3 style={{ margin: '0 0 4px', color: '#1e3a8a', fontSize: '14px', fontWeight: '700' }}>Admin 인증</h3>
        <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '12px' }}>Admin 메뉴 접근을 위해 비밀번호를 입력하세요.</p>
        <form onSubmit={handleSubmit}>
          <input
            type="password" value={pwd} onChange={e => setPwd(e.target.value)}
            placeholder="비밀번호" autoFocus required
            style={{ width: '100%', padding: '8px 10px', border: '2px solid #d1d5db', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box', marginBottom: '12px' }}
          />
          {error && (
            <div style={{ padding: '8px', backgroundColor: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', color: '#991b1b', fontSize: '12px', marginBottom: '12px' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={onClose} style={{
              flex: 1, padding: '8px', backgroundColor: '#6b7280', color: 'white',
              border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
            }}>취소</button>
            <button type="submit" disabled={submitting} style={{
              flex: 1, padding: '8px', backgroundColor: submitting ? '#93c5fd' : '#2563eb', color: 'white',
              border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: submitting ? 'not-allowed' : 'pointer',
            }}>{submitting ? '확인 중...' : '확인'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// 모바일 판정 — 2줄 탭 메뉴는 폰 폭에서 가로로 넘쳐 잘린다 → 768px 미만은 햄버거 + 서랍 메뉴
const MOBILE_QUERY = '(max-width: 767px)';
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}

function AppContent() {
  const { user, portfolios, selectedPfId, selectPortfolio, logout, loading } = useAuth();
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // 서랍에서 펼친 대메뉴 — 열 때마다 현재 화면의 그룹만 펼친다 (항목 40개를 한 번에 늘어놓지 않도록)
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  // 새로고침 시 현재 화면 유지 — localStorage 에 저장
  // Admin 잠금 대상. datacollection/watchlist/cronjob 은 Data 메뉴로 이동하여 잠금 제외.
  const adminPages: PageKey[] = ['portfoliomanagement', 'acctadmin', 'usermanagement', 'changepassword'];
  const [currentPage, setCurrentPageState] = useState<PageKey>(() => {
    const saved = localStorage.getItem('fds_currentPage') as PageKey | null;
    // Admin 페이지로 저장된 경우 기본 페이지로 복원 (인증 전 진입 방지)
    if (saved && adminPages.includes(saved)) return 'orderposition';
    return saved ?? 'orderposition';
  });
  const setCurrentPage = (k: PageKey) => {
    localStorage.setItem('fds_currentPage', k);
    setCurrentPageState(k);
  };
  const fdsIsReal = localStorage.getItem('fds_isReal') === 'true';
  const [showChangePw, setShowChangePw] = useState(false);
  // Admin 메뉴 진입 인증
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [showAdminPwd, setShowAdminPwd] = useState(false);
  const [pendingAdminPage, setPendingAdminPage] = useState<PageKey | null>(null);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 50%, #3b82f6 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <div style={{ color: 'white', fontSize: '14px' }}>로딩 중...</div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  const hasPortfolios = portfolios.length > 0;

  // 계좌 없으면: Dev → Admin 화면, 그 외 → 안내 (2026-10-27)
  //   계좌 등록(KIS 키)은 Dev 만 할 수 있게 바뀌었다 — 외부 사용자는 관리자가 계정관리에서 계좌 권한을 준다.
  if (!hasPortfolios && (user?.role ?? 0) < 90) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '24px 28px', maxWidth: 420, fontSize: '12px', color: '#4b5563' }}>
          <div style={{ color: '#1e40af', fontWeight: 600, marginBottom: 8 }}>사용할 수 있는 계좌가 없습니다</div>
          <div>{user?.email} 계정에 허락된 계좌가 없습니다. 관리자에게 계좌 권한을 요청하세요.</div>
          <button onClick={logout} style={{ marginTop: 16, padding: '5px 12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}>로그아웃</button>
        </div>
      </div>
    );
  }

  // 계좌 없으면 Admin만 표시 (Dev/Admin)
  // 조회 전용(Viewer=5)은 Admin 메뉴 자체를 숨긴다 — 서버도 ViewerReadOnlyMiddleware 가 막는다
  // 조회 전용(5)·API 주문 전용(15)은 Admin 메뉴 자체를 숨긴다 — 서버도 막는다 (2026-10-27)
  const isViewer = user.role === 5 || user.role === 15;
  // Dev 가 아니면 Admin 은 비밀번호변경만 — KIS계정관리·계정관리는 서버가 Dev 만 허용한다 (2026-10-27)
  const roleMenus = user.role >= 90 ? menuGroups : menuGroups.map(g => g.key !== 'admin' ? g
    : { ...g, items: g.items.filter(i => i.key === 'changepassword') });
  const visibleMenuGroups = isViewer
    ? roleMenus.filter(g => g.key !== 'admin')
    : hasPortfolios ? roleMenus : roleMenus.filter(g => g.key === 'admin');
  // 계좌 없어 Admin만 표시되는 경우 잠금 불필요
  const adminOnlyMode = visibleMenuGroups.length === 1 && visibleMenuGroups[0].key === 'admin';

  // 현재 페이지가 보이지 않는 메뉴면 첫 페이지로 이동
  // Admin 잠금 상태에서 Admin 페이지가 currentPage면 비Admin 첫 페이지로 이동
  const isPageVisible = visibleMenuGroups.some(g => g.items.some(i => i.key === currentPage));
  const isAdminPage = adminPages.includes(currentPage);
  if (!isPageVisible && visibleMenuGroups.length > 0) {
    setCurrentPage(visibleMenuGroups[0].items[0].key);
  } else if (isAdminPage && !adminUnlocked && !adminOnlyMode) {
    const nonAdminGroup = visibleMenuGroups.find(g => g.key !== 'admin');
    if (nonAdminGroup) setCurrentPage(nonAdminGroup.items[0].key);
  }

  const activeGroup = visibleMenuGroups.find(g => g.items.some(i => i.key === currentPage)) ?? visibleMenuGroups[0];
  const currentItem = activeGroup.items.find(i => i.key === currentPage);

  // 메뉴 항목 선택 — PC 탭·모바일 서랍 공용 (비밀번호 변경 모달 · Admin 잠금 처리)
  const openItem = (groupKey: string, key: PageKey) => {
    if (key === 'changepassword') {
      setShowChangePw(true);
    } else if (groupKey === 'admin' && !adminUnlocked && !adminOnlyMode) {
      setPendingAdminPage(key);
      setShowAdminPwd(true);
    } else {
      setCurrentPage(key);
    }
    setDrawerOpen(false);
  };

  const accountSelect = portfolios.length >= 1 && (
    <select
      value={selectedPfId ?? ''}
      onChange={e => selectPortfolio(Number(e.target.value))}
      style={{
        padding: '3px 6px',
        borderRadius: '4px',
        border: '1px solid rgba(255,255,255,0.3)',
        backgroundColor: 'rgba(255,255,255,0.15)',
        color: 'white',
        fontSize: '11px',
        outline: 'none',
        maxWidth: isMobile ? '100%' : undefined,
      }}
    >
      {portfolios.length > 1 && (
        <option key={0} value={0} style={{ color: '#1e3a8a' }}>전체</option>
      )}
      {portfolios.map(p => (
        <option key={p.id} value={p.id} style={{ color: '#1e3a8a' }}>
          {p.id} {p.idNm || p.htsId || ''} {p.accountNo} ({p.isReal ? '실전' : '모의'})
        </option>
      ))}
    </select>
  );

  const navBg = fdsIsReal ? 'linear-gradient(to right, #2563eb, #1d4ed8)' : 'linear-gradient(to right, #16a34a, #15803d)';
  const activeBg = fdsIsReal ? '#1e3a8a' : '#14532d';

  const modals = (
    <>
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
      {user?.mustChangePassword && <ChangePasswordModal forced onClose={() => {}} />}
      {showAdminPwd && (
        <AdminPasswordModal
          onSuccess={() => {
            setAdminUnlocked(true);
            setShowAdminPwd(false);
            if (pendingAdminPage) {
              setCurrentPage(pendingAdminPage);
              setPendingAdminPage(null);
            }
          }}
          onClose={() => {
            setShowAdminPwd(false);
            setPendingAdminPage(null);
          }}
        />
      )}
    </>
  );

  if (isMobile) {
    return (
      <div className="min-h-screen bg-blue-50">
        {/* 상단 바 — 햄버거 · 현재 화면 · 계좌 */}
        <nav style={{
          background: navBg,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          position: 'sticky',
          top: 0,
          zIndex: 900,
        }}>
          <button
            aria-label="메뉴 열기"
            onClick={() => { setOpenGroups([activeGroup.key]); setDrawerOpen(true); }}
            style={{
              width: 36, height: 36, flexShrink: 0,
              borderRadius: '4px', border: '1px solid rgba(255,255,255,0.3)',
              backgroundColor: 'rgba(255,255,255,0.15)', color: 'white',
              fontSize: '18px', lineHeight: 1, cursor: 'pointer',
            }}
          >
            ☰
          </button>
          <div style={{ flex: 1, minWidth: 0, color: 'white', lineHeight: 1.25 }}>
            <div style={{ fontSize: '11px', opacity: 0.7 }}>{activeGroup.label}</div>
            <div style={{ fontSize: '14px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {currentItem?.label ?? ''}
            </div>
          </div>
        </nav>

        {/* 서랍 메뉴 — 대메뉴를 제목으로, 하위 메뉴를 세로 목록으로 전부 펼친다 */}
        {drawerOpen && (
          <div
            onClick={() => setDrawerOpen(false)}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 950 }}
          >
            <aside
              onClick={e => e.stopPropagation()}
              style={{
                position: 'absolute', top: 0, left: 0, bottom: 0,
                width: 'min(300px, 85vw)', background: navBg,
                display: 'flex', flexDirection: 'column',
                boxShadow: '4px 0 16px rgba(0,0,0,0.3)',
              }}
            >
              <div style={{ padding: '12px', borderBottom: '1px solid rgba(255,255,255,0.2)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: 'white', fontSize: '13px', fontWeight: 600 }}>{user.name}</span>
                  <button
                    aria-label="메뉴 닫기"
                    onClick={() => setDrawerOpen(false)}
                    style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer', lineHeight: 1 }}
                  >
                    ×
                  </button>
                </div>
                {accountSelect}
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
                {visibleMenuGroups.map(group => {
                  const expanded = openGroups.includes(group.key);
                  return (
                  <div key={group.key}>
                    <button
                      onClick={() => setOpenGroups(g => expanded ? g.filter(k => k !== group.key) : [...g, group.key])}
                      style={{
                        display: 'flex', width: '100%', alignItems: 'center', gap: '8px',
                        padding: '12px 16px', background: 'transparent', border: 'none',
                        color: 'white', fontSize: '13px', fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                        opacity: activeGroup.key === group.key ? 1 : 0.85,
                      }}
                    >
                      <span style={{ width: 12, fontSize: '11px' }}>{expanded ? '▾' : '▸'}</span>
                      <span style={{ flex: 1 }}>
                        {group.label}{group.key === 'admin' && !adminUnlocked && !adminOnlyMode ? ' 🔒' : ''}
                      </span>
                      <span style={{ fontSize: '11px', opacity: 0.6, fontWeight: 400 }}>{group.items.length}</span>
                    </button>
                    {expanded && group.items.map(item => (
                      <button
                        key={item.key}
                        onClick={() => openItem(group.key, item.key)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '10px 16px 10px 36px',
                          backgroundColor: currentPage === item.key ? activeBg : 'transparent',
                          color: 'white', border: 'none', cursor: 'pointer',
                          fontSize: '13px', fontWeight: currentPage === item.key ? 600 : 400,
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                  );
                })}
              </div>

              <div style={{ padding: '12px', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                <button
                  onClick={logout}
                  style={{
                    width: '100%', padding: '8px', borderRadius: '4px',
                    border: '1px solid rgba(255,255,255,0.3)', backgroundColor: 'transparent',
                    color: 'white', fontSize: '12px', cursor: 'pointer',
                  }}
                >
                  로그아웃
                </button>
              </div>
            </aside>
          </div>
        )}

        <FdsNavProvider navigate={setCurrentPage}>
          <main className="fds-mobile-main p-2 bg-blue-50">
            {pageComponents[currentPage]}
          </main>
        </FdsNavProvider>

        {modals}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blue-50">
      {/* 대메뉴 (1행) */}
      <nav style={{
        background: fdsIsReal
          ? 'linear-gradient(to right, #2563eb, #1d4ed8)'
          : 'linear-gradient(to right, #16a34a, #15803d)',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        padding: '6px 20px 0',
        display: 'flex',
        gap: '4px',
        alignItems: 'flex-end',
      }}>
        <div style={{ display: 'flex', gap: '4px', flex: 1 }}>
          {visibleMenuGroups.map(group => (
            <button
              key={group.key}
              onClick={() => {
                if (group.key === 'admin' && !adminUnlocked && !adminOnlyMode) {
                  setPendingAdminPage(group.items[0].key);
                  setShowAdminPwd(true);
                } else {
                  setCurrentPage(group.items[0].key);
                }
              }}
              style={{
                padding: '6px 14px',
                borderRadius: '4px 4px 0 0',
                fontWeight: '500',
                transition: 'all 0.2s',
                backgroundColor: activeGroup.key === group.key
                  ? (fdsIsReal ? '#1e3a8a' : '#14532d')
                  : (fdsIsReal ? '#3b82f6' : '#22c55e'),
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
              }}
              onMouseEnter={(e) => {
                if (activeGroup.key !== group.key) {
                  e.currentTarget.style.backgroundColor = fdsIsReal ? '#60a5fa' : '#4ade80';
                }
              }}
              onMouseLeave={(e) => {
                if (activeGroup.key !== group.key) {
                  e.currentTarget.style.backgroundColor = fdsIsReal ? '#3b82f6' : '#22c55e';
                }
              }}
            >
              {group.label}
            </button>
          ))}
        </div>

        {/* 우측: 사용자 정보 + 포트폴리오 선택 + 로그아웃 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingBottom: '8px' }}>
          {portfolios.length >= 1 && (
            <>
              <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px' }}>계좌:</span>
              {accountSelect}
            </>
          )}
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>|</span>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px' }}>
            {user.name}
          </span>
          <button
            onClick={logout}
            style={{
              padding: '3px 10px',
              borderRadius: '4px',
              border: '1px solid rgba(255,255,255,0.3)',
              backgroundColor: 'transparent',
              color: 'white',
              fontSize: '11px',
              cursor: 'pointer',
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            로그아웃
          </button>
        </div>
      </nav>

      {/* 서브메뉴 (2행) */}
      <nav style={{
        backgroundColor: fdsIsReal ? '#1d4ed8' : '#15803d',
        padding: '8px 20px',
        display: 'flex',
        gap: '8px',
      }}>
        {activeGroup.items.map(item => (
          <button
            key={item.key}
            onClick={() => openItem(activeGroup.key, item.key)}
            style={{
              padding: '6px 14px',
              borderRadius: '4px',
              fontWeight: '500',
              transition: 'all 0.2s',
              backgroundColor: currentPage === item.key ? (fdsIsReal ? '#1e3a8a' : '#14532d') : 'transparent',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              fontSize: '12px',
            }}
            onMouseEnter={(e) => {
              if (currentPage !== item.key) {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (currentPage !== item.key) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {/* 메인 컨텐츠 */}
      <FdsNavProvider navigate={setCurrentPage}>
        <main className="p-6 bg-blue-50">
          {pageComponents[currentPage]}
        </main>
      </FdsNavProvider>

      {modals}
    </div>
  );
}

function App() {
  return <AppContent />;
}

export default App;
